/**
 * Rewrites nectios-app notification targetURLs into legacy Vue routes.
 *
 * New app: /s/{slug}/..., /admin/, /account/..., root-level main-platform paths
 * Legacy:  /{slug}/..., /backoffice/, /myself/...
 */

const ROOT_MAIN_PLATFORM_SEGMENTS = new Set([
  'forums',
  'posts',
  'questions',
  'blogs',
  'events',
  'members',
  'organizations',
  'challenges',
  'projects',
  'ideas',
  'articles',
]);

function appendSearchAndHash(path, search, hash) {
  return `${path}${search || ''}${hash || ''}`;
}

function normalizeUsername(rawUsername) {
  if (!rawUsername) return '';
  const withoutLegacySuffix = String(rawUsername).split('&')[0].split('?')[0].replace(/\/+$/, '');
  try {
    return decodeURIComponent(withoutLegacySuffix);
  } catch {
    return withoutLegacySuffix;
  }
}

/**
 * @returns {{ path: string, search: string }}
 */
function rewriteBackofficeMembersPath(slug, searchParams) {
  const username = normalizeUsername(searchParams.get('username'));
  const nextParams = new URLSearchParams(searchParams);
  nextParams.delete('username');
  const remaining = nextParams.toString();
  const search = remaining ? `?${remaining}` : '';

  if (username) {
    return {
      path: `/${slug}/backoffice/members/${encodeURIComponent(username)}`,
      search,
    };
  }
  return {
    path: `/${slug}/backoffice/members`,
    search,
  };
}

function rewriteAdminNotificationsPath(slug, searchParams, hash) {
  const hasContentHint = searchParams.has('contentType') || searchParams.has('contentKey');
  const path = hasContentHint
    ? `/${slug}/backoffice/moderation`
    : `/${slug}/backoffice/members`;
  const search = searchParams.toString();
  return appendSearchAndHash(path, search ? `?${search}` : '', hash);
}

function rewritePathname(pathname, search, hash, mainSlug) {
  const path = pathname || '/';
  const searchParams = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);

  // /s/{slug}/admin/notifications
  const adminNotificationsMatch = path.match(/^\/s\/([^/]+)\/admin\/notifications\/?$/i);
  if (adminNotificationsMatch) {
    return rewriteAdminNotificationsPath(adminNotificationsMatch[1], searchParams, hash);
  }

  // /s/{slug}/admin/members(?username=)
  const adminMembersMatch = path.match(/^\/s\/([^/]+)\/admin\/members\/?$/i);
  if (adminMembersMatch) {
    const rewritten = rewriteBackofficeMembersPath(adminMembersMatch[1], searchParams);
    return appendSearchAndHash(rewritten.path, rewritten.search, hash);
  }

  // /s/{slug}/admin/{rest}
  const adminMatch = path.match(/^\/s\/([^/]+)\/admin(?:\/(.*))?$/i);
  if (adminMatch) {
    const rest = adminMatch[2] ? `/${adminMatch[2].replace(/^\/+/, '')}` : '';
    return appendSearchAndHash(`/${adminMatch[1]}/backoffice${rest}`, search, hash);
  }

  // /s/{slug}/forum/{key} (singular) → /{slug}/forums/{key}
  const singularForumMatch = path.match(/^\/s\/([^/]+)\/forum(?:\/(.*))?$/i);
  if (singularForumMatch) {
    const rest = singularForumMatch[2] ? `/${singularForumMatch[2].replace(/^\/+/, '')}` : '';
    return appendSearchAndHash(`/${singularForumMatch[1]}/forums${rest}`, search, hash);
  }

  // /s/{slug}/{rest}
  const spaceMatch = path.match(/^\/s\/([^/]+)(?:\/(.*))?$/i);
  if (spaceMatch) {
    const rest = spaceMatch[2] ? `/${spaceMatch[2].replace(/^\/+/, '')}` : '';
    return appendSearchAndHash(`/${spaceMatch[1]}${rest}`, search, hash);
  }

  // Root /backoffice/notifications|moderation → /{mainSlug}/backoffice/moderation
  if (/^\/backoffice\/(?:notifications|moderation)\/?$/i.test(path)) {
    if (!mainSlug) return appendSearchAndHash(path, search, hash);
    return appendSearchAndHash(`/${mainSlug}/backoffice/moderation`, search, hash);
  }

  // /backoffice/members(?username=)
  if (/^\/backoffice\/members\/?$/i.test(path)) {
    if (!mainSlug) return appendSearchAndHash(path, search, hash);
    const rewritten = rewriteBackofficeMembersPath(mainSlug, searchParams);
    return appendSearchAndHash(rewritten.path, rewritten.search, hash);
  }

  // /backoffice/{rest}
  const backofficeMatch = path.match(/^\/backoffice(?:\/(.*))?$/i);
  if (backofficeMatch) {
    if (!mainSlug) return appendSearchAndHash(path, search, hash);
    const rest = backofficeMatch[1] ? `/${backofficeMatch[1].replace(/^\/+/, '')}` : '';
    return appendSearchAndHash(`/${mainSlug}/backoffice${rest}`, search, hash);
  }

  // /account/notifications or /notifications
  if (/^\/(?:account\/)?notifications\/?$/i.test(path)) {
    if (!mainSlug) return appendSearchAndHash(path, search, hash);
    return appendSearchAndHash(`/${mainSlug}/myself/notifications`, search, hash);
  }

  // Root-level nectios-app main-platform segments → /{mainSlug}/{path}
  const rootSegmentMatch = path.match(/^\/([a-z0-9-]+)(?:\/(.*))?$/i);
  if (rootSegmentMatch && ROOT_MAIN_PLATFORM_SEGMENTS.has(rootSegmentMatch[1].toLowerCase())) {
    if (!mainSlug) return appendSearchAndHash(path, search, hash);
    const rest = rootSegmentMatch[2] ? `/${rootSegmentMatch[2].replace(/^\/+/, '')}` : '';
    return appendSearchAndHash(`/${mainSlug}/${rootSegmentMatch[1]}${rest}`, search, hash);
  }

  return appendSearchAndHash(path, search, hash);
}

/**
 * Pure rewrite of a notification targetURL to a legacy Vue path/URL.
 * @param {string|null|undefined} targetURL
 * @param {{ mainSlug?: string, currentOrigin?: string }} [options]
 * @returns {string}
 */
export function toLegacyTargetUrl(targetURL, options = {}) {
  if (targetURL == null || typeof targetURL !== 'string') return '';
  const trimmed = targetURL.trim();
  if (!trimmed) return '';

  const mainSlug = options.mainSlug || '';
  const currentOrigin = options.currentOrigin
    || (typeof window !== 'undefined' && window.location && window.location.origin)
    || '';

  // Absolute URL
  if (/^https?:\/\//i.test(trimmed)) {
    let parsed;
    try {
      parsed = new URL(trimmed);
    } catch {
      return trimmed;
    }

    const isSameOrigin = currentOrigin && parsed.origin === currentOrigin;
    const looksLikeAppPath = /^\/(s\/|backoffice(?:\/|$)|account(?:\/|$)|admin(?:\/|$))/i.test(parsed.pathname);
    if (!isSameOrigin && !looksLikeAppPath) {
      return trimmed;
    }

    const rewrittenPath = rewritePathname(parsed.pathname, parsed.search, parsed.hash, mainSlug);
    if (isSameOrigin || !currentOrigin) {
      // Keep absolute form when input was absolute and same-origin
      try {
        return new URL(rewrittenPath, parsed.origin).toString();
      } catch {
        return rewrittenPath;
      }
    }
    // Cross-origin but app-shaped path: rewrite to relative legacy path
    return rewrittenPath;
  }

  // Root-relative path
  if (trimmed.startsWith('/')) {
    let parsed;
    try {
      parsed = new URL(trimmed, currentOrigin || 'https://placeholder.local');
    } catch {
      return trimmed;
    }
    return rewritePathname(parsed.pathname, parsed.search, parsed.hash, mainSlug);
  }

  return trimmed;
}

function resolveMainSlugFromStore(store) {
  const mainSlug = store?.getters?.mainCollective?.slug;
  if (mainSlug && typeof mainSlug === 'string') return mainSlug;

  const currentSlug = store?.getters?.currentCollective?.slug;
  if (currentSlug && typeof currentSlug === 'string') return currentSlug;
  if (currentSlug && typeof currentSlug === 'object' && currentSlug.slug) {
    return String(currentSlug.slug);
  }

  if (typeof window !== 'undefined' && window.location?.pathname) {
    const first = window.location.pathname.split('/').filter(Boolean)[0];
    if (first && first !== 's') return first;
  }

  return '';
}

/**
 * Store-aware wrapper used by Vue components.
 * @param {string|null|undefined} targetURL
 * @returns {string}
 */
export function resolveNotificationTargetUrl(targetURL) {
  // Lazy require to avoid circular imports at module load time
  // eslint-disable-next-line global-require
  const store = require('@/store').default;
  const mainSlug = resolveMainSlugFromStore(store);
  return toLegacyTargetUrl(targetURL, { mainSlug });
}

export { ROOT_MAIN_PLATFORM_SEGMENTS };
